import { ICrumb, ISelectOption } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import Constants from '../constants';
import {
	AudienceSourceType,
	AudienceStatus,
	DistributionType,
	IAudience,
	IBenchmark,
	IHierarchyMetadata,
	SurveyStatusType,
} from '../models';

export const SurveyHelper = {
	getSelectedCultures(cultureCodes: string[], availableCultures: ISelectOption[]): ISelectOption[] {
		return availableCultures
			.filter((ac: ISelectOption) => {
				return cultureCodes.some((cc: string) => cc === ac.name);
			})
			.map((c) => {
				return { ...c, disabled: c.name === Constants.DEFAULT_CULTURE_CODE };
			});
	},
	getDefaultCulture(defaultCultureCode: string, availableCultures: ISelectOption[]): ISelectOption {
		const defaultCulture = availableCultures.find((ac: ISelectOption) => ac.name === defaultCultureCode) as ISelectOption;
		return { ...defaultCulture, disabled: true };
	},
	getSelectedAdditionalCultures(cultureCodes: string[], defaultCultureCode: string, availableCultures: ISelectOption[]): ISelectOption[] {
		return availableCultures.filter((ac: ISelectOption) => {
			return cultureCodes.some((cc: string) => cc === ac.name) && ac.name !== defaultCultureCode;
		});
	},
	getSelectedBenchmarks(benchmarkIds: number[], availableBenchmarks: ISelectOption[]): ISelectOption[] {
		return availableBenchmarks.filter((ac: ISelectOption) => {
			return benchmarkIds.some((id: number) => id.toString() === ac.name);
		});
	},

	getCrumbsFromBenchmarks(
		benchmarkIds: number[],
		availableBenchmarks: IBenchmark[],
		globalTopQuartileId?: number,
		readOnly?: boolean,
	): ICrumb[] {
		return _.map(benchmarkIds, (b: number, counter: number) => {
			const benchmark = _.find(availableBenchmarks, (a: IBenchmark) => a.benchmarkId === b);
			return {
				value: benchmark ? benchmark.benchmarkId.toString() : '',
				displayText: benchmark ? benchmark.displayText : '',
				sequence: counter,
				isDismissible: !readOnly && !(benchmark && benchmark.benchmarkId === globalTopQuartileId),
			};
		});
	},
	getCrumbsFromSelectOptions(items: ISelectOption[], selectedValues?: string[]): ICrumb[] {
		const selectedItems = selectedValues !== undefined ? (items = items.filter((i) => selectedValues.indexOf(i.name) > -1)) : items;
		return selectedItems.map(this.getCrumbFromSelectOption);
	},
	getCrumbFromSelectOption(option: ISelectOption): ICrumb {
		return {
			value: option.name,
			displayText: option.displayText,
			sequence: option.displayOrder,
			isDismissible: option.disabled === false,
		};
	},
	getSelectOptionsFromAttributes(attributes: string[]): ISelectOption[] {
		return _.map(attributes, (a: string, counter: number) => {
			return {
				name: a,
				displayText: a,
				displayOrder: counter + 1,
				disabled: false,
			};
		});
	},
	getCrumbsFromAttributes(attributeModels: string[], availableAttributes: string[], readOnly?: boolean): ICrumb[] {
		return _.map(attributeModels, (a: string, counter: number) => {
			const attribute = _.find(
				availableAttributes,
				(b: string) => b.toUpperCase().localeCompare(a.toUpperCase(), undefined, { sensitivity: 'accent' }) === 0,
			);

			if (attribute !== undefined) {
				return {
					value: attribute,
					displayText: attribute,
					sequence: counter,
					isDismissible: !readOnly,
				};
			}
			return {
				value: a,
				displayText: a,
				sequence: counter,
				isDismissible: !readOnly,
			};
		});
	},
	getSelectOptionsFromHierarchyAttributes(hierarchies: IHierarchyMetadata[]): ISelectOption[] {
		return _.map(hierarchies, (h: IHierarchyMetadata, counter: number) => {
			return {
				name: h.hierarchyId,
				displayText: h.name,
				displayOrder: counter + 1,
				disabled: false,
			};
		});
	},
	getCrumbsFromHierarchyAttributes(
		selectedHierarchies: string[],
		availableHierarchies: IHierarchyMetadata[],
		readOnly?: boolean,
	): ICrumb[] {
		return _.map(selectedHierarchies, (h: string, counter: number) => {
			const hierarchy = _.find(availableHierarchies, (hier: IHierarchyMetadata) => hier.hierarchyId === h);

			if (hierarchy !== undefined) {
				return {
					value: hierarchy.hierarchyId,
					displayText: hierarchy.name,
					sequence: counter,
					isDismissible: !readOnly,
				};
			}
			return {
				value: h,
				displayText: h,
				sequence: counter,
				isDismissible: !readOnly,
			};
		});
	},
	getSurveyStatusName(statusType: SurveyStatusType): string {
		switch (statusType) {
			case SurveyStatusType.SchedulingInProgress:
				return 'Scheduling in Progress';
			case SurveyStatusType.SurveyInProgress:
				return 'Survey in Progress';
			case SurveyStatusType.SurveyClosed:
				return 'Survey Closed';
			case SurveyStatusType.PublicSurveyTemplate:
				return 'Public Survey Template';
			case SurveyStatusType.DeletingInProgress:
				return 'Deleting in Progress';
			case SurveyStatusType.ClosingInProgress:
				return 'Closing in Progress';
			case SurveyStatusType.SurveyCloseFailed:
				return 'Failed to Close';
			default:
				return SurveyStatusType[statusType];
		}
	},
	getSurveyOpenDate(audiences: IAudience[]): string | undefined {
		const earliestAudience = this.getEarliestAudience(audiences);
		return earliestAudience?.openOn;
	},
	getSurveyCreatedDate(audiences: IAudience[]): Date | undefined {
		const earliestAudience = this.getEarliestAudience(audiences);
		return earliestAudience?.createdOn;
	},
	getSurveyCloseDate(audiences: IAudience[]): string | undefined {
		const latestAudience = _.reduce(this.getActiveAudiences(audiences), (prev, cur) => {
			if (prev.closeOn && cur.closeOn) {
				return Date.parse(prev.closeOn) < Date.parse(cur.closeOn) ? cur : prev;
			} else if (prev.closeOn) {
				return prev;
			} else {
				return cur;
			}
		});
		return latestAudience?.closeOn;
	},
	getEarliestAudience(audiences: IAudience[]) {
		return _.reduce(this.getActiveAudiences(audiences), (prev, cur) => {
			if (prev.openOn && cur.openOn) {
				return Date.parse(prev.openOn) > Date.parse(cur.openOn) ? cur : prev;
			} else if (prev.openOn) {
				return prev;
			} else {
				return cur;
			}
		});
	},
	getActiveAudiences(audiences: IAudience[]) {
		return _.filter(
			audiences,
			(audience) =>
				!audience.isTestAudience &&
				(audience.audienceStatus === AudienceStatus.Publishing ||
					audience.audienceStatus === AudienceStatus.Queued ||
					audience.audienceStatus === AudienceStatus.Live ||
					audience.audienceStatus === AudienceStatus.Closed ||
					audience.audienceStatus === AudienceStatus.Ready),
		);
	},
	getSurveyIsClosed(audiences: IAudience[]): boolean {
		return _.every(this.getActiveAudiences(audiences), (a) => a.isClosed);
	},
	getDistributionType(audiences: IAudience[]): DistributionType | undefined {
		let dt = _.find(audiences, (a) => a.distributionType === DistributionType.Unique)?.distributionType;
		if (dt === undefined) {
			dt = _.find(audiences, (a) => a.distributionType === DistributionType.SingleLinkWithAccessCode)?.distributionType;
			if (dt === undefined) {
				dt = _.find(audiences, (a) => a.distributionType === DistributionType.SSO)?.distributionType;
				if (dt === undefined) {
					return _.find(audiences, (a) => a.distributionType !== undefined)?.distributionType;
				}
			}
		}
		return dt;
	},
	getAudienceSourceType(audiences: IAudience[]): AudienceSourceType | undefined {
		return _.find(audiences, (a) => a.audienceSourceType !== undefined)?.audienceSourceType;
	},
};
